import { LoadingOverlay, ScrollArea, Stack, Tabs } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRobot, updateRobotConfigs, RobotRequestConfigs } from '@mygpt/app/apis';
import { useBot } from '@mygpt/app/bot';
import {
  GEMINI_REALTIME_DEFAULT_MODEL,
  OPENAI_REALTIME_DEFAULT_MODEL,
  OPENAI_REASONING_EFFORT_DEFAULT,
  OPENAI_REALTIME_TOOL_MODE_DEFAULT,
  REALTIME_LANGUAGE_MODE_DEFAULT,
  resolveRealtimeVoice,
  type RealtimeProvider,
} from '@mygpt/app/chat/types/realtime';

import ContainerHeader from '@/components/Headers/ContainerHeader';
import { AccessControl } from './AccessControl';
import RealtimePrompt from './RealtimePrompt';
import { ShareLink } from './ShareLink';

// 表单数据类型
export interface RealtimeFormValues {
  enable_realtime_access_control: boolean;
  realtime_access_control_allow_email_list: string[];
  realtime_model: string;
  gemini_realtime_model: string;
  openai_realtime_model: string;
  openai_live_voice_prompt: string;
  openai_realtime_reasoning_effort: string;
  openai_realtime_tool_mode: string;
  realtime_language_mode: string;
  voice_thinking_level: string;
  voice_tool_mode: string;
  show_realtime_in_share: boolean;
  realtime_default_voices: Partial<Record<RealtimeProvider, string>>;
  enable_realtime_consent: boolean;
  realtime_input_user_identifier: boolean;
  enable_realtime_session_prompt: boolean;
}

const Realtime = () => {
  const { t } = useTranslation();
  const { botId } = useBot();

  const [loading, setLoading] = useState(true);

  const form = useForm<RealtimeFormValues>({
    initialValues: {
      enable_realtime_access_control: false,
      realtime_access_control_allow_email_list: [],
      realtime_model: 'gemini',
      gemini_realtime_model: GEMINI_REALTIME_DEFAULT_MODEL,
      openai_realtime_model: OPENAI_REALTIME_DEFAULT_MODEL,
      openai_live_voice_prompt: '',
      openai_realtime_reasoning_effort: OPENAI_REASONING_EFFORT_DEFAULT,
      openai_realtime_tool_mode: OPENAI_REALTIME_TOOL_MODE_DEFAULT,
      realtime_language_mode: REALTIME_LANGUAGE_MODE_DEFAULT,
      voice_thinking_level: 'minimal',
      voice_tool_mode: 'classic',
      show_realtime_in_share: false,
      realtime_default_voices: {},
      enable_realtime_consent: false,
      realtime_input_user_identifier: true,
      enable_realtime_session_prompt: false,
    },
  });

  // 加载当前配置
  useEffect(() => {
    if (!botId) return;

    const loadSettings = async () => {
      try {
        setLoading(true);
        const { data } = await getRobot({ aiId: botId });

        form.setValues({
          enable_realtime_access_control: data.configs?.enable_realtime_access_control ?? false,
          realtime_access_control_allow_email_list: data.configs?.realtime_access_control_allow_email_list || [],
          realtime_model: data.configs?.realtime_model || 'gemini',
          gemini_realtime_model: data.configs?.gemini_realtime_model || GEMINI_REALTIME_DEFAULT_MODEL,
          openai_realtime_model: data.configs?.openai_realtime_model || OPENAI_REALTIME_DEFAULT_MODEL,
          openai_live_voice_prompt: data.configs?.openai_live_voice_prompt || '',
          openai_realtime_reasoning_effort:
            data.configs?.openai_realtime_reasoning_effort || OPENAI_REASONING_EFFORT_DEFAULT,
          openai_realtime_tool_mode: data.configs?.openai_realtime_tool_mode || OPENAI_REALTIME_TOOL_MODE_DEFAULT,
          realtime_language_mode: data.configs?.realtime_language_mode || REALTIME_LANGUAGE_MODE_DEFAULT,
          voice_thinking_level: data.configs?.voice_thinking_level || 'minimal',
          voice_tool_mode: data.configs?.voice_tool_mode || 'classic',
          show_realtime_in_share: data.configs?.show_realtime_in_share ?? false,
          realtime_default_voices: data.configs?.realtime_default_voices || {},
          enable_realtime_consent: data.configs?.enable_realtime_consent ?? false,
          realtime_input_user_identifier: data.configs?.realtime_input_user_identifier ?? true,
          enable_realtime_session_prompt: data.configs?.enable_realtime_session_prompt ?? false,
        });
      } catch (error) {
        console.error('加载 Realtime 设置失败:', error);
        showNotification({
          message: t('settings.realtime.load_error'),
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [botId]);

  // 处理访问控制变更
  const handleAccessControlChange = async (value: boolean, emailList?: string[]) => {
    try {
      const configs: Partial<RobotRequestConfigs> = {
        enable_realtime_access_control: value,
        realtime_access_control_allow_email_list:
          emailList !== undefined ? emailList : form.values.realtime_access_control_allow_email_list,
      };

      await updateRobotConfigs({
        aiId: botId!,
        configs,
      });

      showNotification({
        message: t('save_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('保存 Realtime 设置失败:', error);
      showNotification({
        message: t('settings.realtime.save_error'),
        color: 'red',
      });
    }
  };

  // 处理 Gemini 专属配置变更（模型版本 / 思考程度 / 工具响应模式）
  const handleGeminiConfigChange = async (
    key: 'gemini_realtime_model' | 'voice_thinking_level' | 'voice_tool_mode',
    value: string
  ) => {
    form.setFieldValue(key, value);

    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { [key]: value },
      });

      showNotification({
        message: t('save_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('保存 Gemini 配置失败:', error);
      showNotification({
        message: t('settings.realtime.save_error'),
        color: 'red',
      });
    }
  };

  // Realtime 使用前同意声明开关
  const handleEnableConsentChange = async (value: boolean) => {
    form.setFieldValue('enable_realtime_consent', value);
    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { enable_realtime_consent: value },
      });
      showNotification({ message: t('save_success'), color: 'green' });
    } catch (error) {
      console.error('保存 Realtime 同意声明开关失败:', error);
      showNotification({ message: t('settings.realtime.save_error'), color: 'red' });
    }
  };

  // Realtime 打开时是否要求输入昵称
  const handleInputUserIdentifierChange = async (value: boolean) => {
    form.setFieldValue('realtime_input_user_identifier', value);
    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { realtime_input_user_identifier: value },
      });
      showNotification({ message: t('save_success'), color: 'green' });
    } catch (error) {
      console.error('保存 Realtime 昵称输入开关失败:', error);
      showNotification({ message: t('settings.realtime.save_error'), color: 'red' });
    }
  };

  // 是否允许用户在 Realtime 页临时设置会话级 prompt
  const handleEnableSessionPromptChange = async (value: boolean) => {
    form.setFieldValue('enable_realtime_session_prompt', value);
    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { enable_realtime_session_prompt: value },
      });
      showNotification({ message: t('save_success'), color: 'green' });
    } catch (error) {
      console.error('保存 Realtime 会话级 prompt 开关失败:', error);
      showNotification({ message: t('settings.realtime.save_error'), color: 'red' });
    }
  };

  // 分享页是否显示 realtime 入口按钮
  const handleShowRealtimeInShareChange = async (value: boolean) => {
    form.setFieldValue('show_realtime_in_share', value);
    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { show_realtime_in_share: value },
      });
      showNotification({ message: t('save_success'), color: 'green' });
    } catch (error) {
      console.error('保存分享页 realtime 开关失败:', error);
      showNotification({ message: t('settings.realtime.save_error'), color: 'red' });
    }
  };

  // 某个 provider 的默认音色变更
  const handleDefaultVoiceChange = async (provider: RealtimeProvider, voice: string) => {
    const next = { ...form.values.realtime_default_voices, [provider]: voice };
    form.setFieldValue('realtime_default_voices', next);
    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { realtime_default_voices: next },
      });
      showNotification({ message: t('save_success'), color: 'green' });
    } catch (error) {
      console.error('保存默认音色失败:', error);
      showNotification({ message: t('settings.realtime.save_error'), color: 'red' });
    }
  };

  // 处理 OpenAI 模型版本变更
  const handleOpenAIModelChange = async (value: string | null) => {
    if (!value) return;

    const currentVoice = form.values.realtime_default_voices.openai;
    const nextVoice = resolveRealtimeVoice('openai', value, currentVoice);
    // 模型与兼容音色在同一请求中保存，避免 Live 专属音色残留在 Realtime 配置中。
    const configs = {
      openai_realtime_model: value,
      ...(currentVoice && nextVoice !== currentVoice
        ? { realtime_default_voices: { ...form.values.realtime_default_voices, openai: nextVoice } }
        : {}),
    };

    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs,
      });
      form.setValues(configs);

      showNotification({
        message: t('save_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('保存 OpenAI 模型版本失败:', error);
      showNotification({
        message: t('settings.realtime.save_error'),
        color: 'red',
      });
    }
  };

  // 处理 gpt-live-1 声音模型提示词变更（失焦保存；与上次保存值相同则跳过）
  const lastSavedLiveVoicePromptRef = useRef<string | null>(null);
  const handleOpenAILiveVoicePromptChange = async (value: string) => {
    form.setFieldValue('openai_live_voice_prompt', value);
    if (lastSavedLiveVoicePromptRef.current === value) return;
    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { openai_live_voice_prompt: value },
      });
      lastSavedLiveVoicePromptRef.current = value;
      showNotification({
        message: t('save_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('保存 gpt-live-1 声音提示词失败:', error);
      showNotification({
        message: t('settings.realtime.save_error'),
        color: 'red',
      });
    }
  };

  // 处理 OpenAI reasoning effort 变更（仅 gpt-realtime-2 起生效）
  const handleOpenAIReasoningEffortChange = async (value: string | null) => {
    if (!value) return;

    form.setFieldValue('openai_realtime_reasoning_effort', value);

    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { openai_realtime_reasoning_effort: value },
      });

      showNotification({
        message: t('save_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('保存 OpenAI reasoning effort 失败:', error);
      showNotification({
        message: t('settings.realtime.save_error'),
        color: 'red',
      });
    }
  };

  // 处理 OpenAI 工具编排模式变更（classic / direct）
  const handleOpenAIToolModeChange = async (value: string | null) => {
    if (!value) return;

    form.setFieldValue('openai_realtime_tool_mode', value);

    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { openai_realtime_tool_mode: value },
      });

      showNotification({
        message: t('save_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('保存 OpenAI 工具编排模式失败:', error);
      showNotification({
        message: t('settings.realtime.save_error'),
        color: 'red',
      });
    }
  };

  // 处理応答言語モード变更（fixed / auto）
  const handleLanguageModeChange = async (value: string | null) => {
    if (!value) return;

    form.setFieldValue('realtime_language_mode', value);

    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { realtime_language_mode: value },
      });

      showNotification({
        message: t('save_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('保存応答言語モード失败:', error);
      showNotification({
        message: t('settings.realtime.save_error'),
        color: 'red',
      });
    }
  };

  // 处理 Provider 变更
  const handleProviderChange = async (value: string | null) => {
    if (!value) return;

    form.setFieldValue('realtime_model', value);

    try {
      await updateRobotConfigs({
        aiId: botId!,
        configs: { realtime_model: value },
      });

      showNotification({
        message: t('save_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('保存 Provider 设置失败:', error);
      showNotification({
        message: t('settings.realtime.save_error'),
        color: 'red',
      });
    }
  };

  return (
    <>
      <LoadingOverlay visible={loading} />
      <ContainerHeader
        title={t('settings.realtime.page_title')}
        description={t('settings.realtime.page_description')}
      />
      <Tabs defaultValue="prompt" className="mt-6">
        <Tabs.List className="mb-4">
          <Tabs.Tab value="prompt">{t('settings.realtime.tab_prompt')}</Tabs.Tab>
          <Tabs.Tab value="share">{t('settings.realtime.tab_share')}</Tabs.Tab>
          <Tabs.Tab value="access">{t('settings.realtime.tab_access')}</Tabs.Tab>
        </Tabs.List>
        <ScrollArea h="calc(100vh - var(--mantine-spacing-md) * 14)">
          <Tabs.Panel value="prompt">
            <RealtimePrompt />
          </Tabs.Panel>
          <Tabs.Panel value="share">
            <Stack gap="lg">
              <ShareLink botId={botId!} form={form} onShowRealtimeInShareChange={handleShowRealtimeInShareChange} />
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value="access">
            <Stack gap="lg">
              <AccessControl
                form={form}
                onValueChange={handleAccessControlChange}
                onProviderChange={handleProviderChange}
                onOpenAIModelChange={handleOpenAIModelChange}
                onOpenAILiveVoicePromptChange={handleOpenAILiveVoicePromptChange}
                onOpenAIReasoningEffortChange={handleOpenAIReasoningEffortChange}
                onOpenAIToolModeChange={handleOpenAIToolModeChange}
                onLanguageModeChange={handleLanguageModeChange}
                onGeminiConfigChange={handleGeminiConfigChange}
                onDefaultVoiceChange={handleDefaultVoiceChange}
                onEnableConsentChange={handleEnableConsentChange}
                onInputUserIdentifierChange={handleInputUserIdentifierChange}
                onEnableSessionPromptChange={handleEnableSessionPromptChange}
              />
            </Stack>
          </Tabs.Panel>
        </ScrollArea>
      </Tabs>
    </>
  );
};

export default Realtime;
